import View                          from '@widesk-ui/components/View';
import { CopyOutlined }              from '@ant-design/icons';
import { SettingOutlined }           from '@ant-design/icons';
import { DownOutlined }              from '@ant-design/icons';
import SwitchField                   from '@/TokamapEditor/ux/SwitchField';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore     from '@/TokamapEditor/stores/currentProjectEditorStore';
import { observer }                  from 'mobx-react-lite';
import Field                         from '@/TokamapEditor/ux/AreaSidebar/Field';
import ColorButton                   from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import SectionTitle                  from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import FunctionalityGate             from '@/components/Functionality';
import { TileLayerName }             from '@tokamap/core';
import { OsmStyleName }              from '@tokamap/core';
import { resolveOsmOptions }         from '@tokamap/core';
import Segmented                     from '@widesk-ui/Segmented';
import React                        from 'react';
import useForceUpdate                from '@widesk-core/hooks/useForceUpdate';
import useDesignTokens               from '@/hooks/useDesignTokens';
import Select                        from '@widesk-ui/components/form/Select';
import { Input }                     from '@widesk-ui/components/form/Input';
import publicProjectLink             from '@/tools/publicProjectLink';
import { message }                   from '@widesk-ui/hooks/useMessage';
import FramingSection                from '@/TokamapEditor/ux/Framing/FramingSection';

export function SettingsSwatch() {
	const tokens = useDesignTokens();

	return (
		<View
			w={46}
			h={46}
			br={8}
			center
			color={tokens.color.primary}
			style={{ background: tokens.surface.subtle, border: `1px solid ${tokens.border.default}`, flexShrink: 0 }}
		>
			<SettingOutlined style={{ fontSize: 22 }} />
		</View>
	);
}

const SettingsSidebar = observer(() => {
	const editor = currentEditorStore.get;
	const projectStore = currentProjectEditorStore.get;
	const tokens = useDesignTokens();

	React.useEffect(() => {
		if (!editor) return;

		return editor.mapAdapter.on((event) => {
			if (
				event.type !== 'geolocation:changed'
				&& event.type !== 'allow-pitch:changed'
				&& event.type !== 'tile-layer:changed'
				&& event.type !== 'osm-options:changed'
				&& event.type !== 'background-color:changed'
			) return;
			forceUpdate();
		});
	}, [editor]);

	const forceUpdate = useForceUpdate();

	// L'adresse publique appartient au PROJET : elle sert toutes ses cartes, pas seulement celle-ci.
	const publicLink = publicProjectLink(projectStore.project);

	return (
		<View p="md" gap="md">
			<View gap="md">
				<SectionTitle>Cadrage</SectionTitle>

				<FramingSection />
			</View>

			<View h={1} bg={tokens.border.default} />

			<View gap="md">
				<SectionTitle>Carte de fond</SectionTitle>

				<FunctionalityGate reference="geolocalisation" fullWidth>
					<SwitchField
						label="Activer la géolocalisation"
						help="affiche un bouton pour centrer la carte sur la position de l'utilisateur et permet de l'utiliser comme point de départ d'un itinéraire."
						value={editor.getGeolocationEnabled()}
						onChange={value => editor.setGeolocationEnabled(value)}
					/>
				</FunctionalityGate>

				{/* Hors des options du fond OpenStreetMap : la 3D vaut aussi pour les volumes des formes, quel que soit le fond. */}
				<SwitchField
					label="Activer la 3D"
					help="autorise l'inclinaison de la caméra pour afficher la carte et les volumes des formes en perspective."
					value={editor.getAllowPitch()}
					onChange={value => {
						// `skipHistory`/`skipSave` fait porter les deux changements par la SEULE commande qui
						// suit (voir `ConfigurationSaveTracker.resolveHistoryBeforeConfiguration`) : un unique
						// Annuler les rétablit ensemble.
						if (!value) editor.setOsmOptions({ buildings3d: false }, { skipHistory: true, skipSave: true });
						editor.setAllowPitch(value);
					}}
				/>

				<Field label="Carte du monde">
					<Select
						value={editor.getTileLayerName()}
						onChange={value => editor.setTileLayerName((value || undefined) as TileLayerName)}
						options={tileLayerOptions}
					/>
				</Field>

				<Field label="Couleur de fond">
					<View row>
						<ColorButton
							label="Fond de la carte"
							value={editor.getBackgroundColor()}
							onChange={color => editor.setBackgroundColor(color)}
						/>
					</View>
				</Field>

				{editor.getTileLayerName() === 'osm' && <AdvancedOptionsSection />}
			</View>

			<View h={1} bg={tokens.border.default} />

			<View gap="md">
				<SectionTitle>Partage</SectionTitle>

				<Field label="Lien public">
					<Input
						value={publicLink}
						disabled
						suffix={(
							<CopyOutlined
								onClick={async () => {
									await navigator.clipboard.writeText(publicLink);
									message.open({ type: 'success', content: 'Lien copié dans le presse-papier.', duration: 2 });
								}}
							/>
						)}
					/>
				</Field>
			</View>
		</View>
	);
});

export default SettingsSidebar;

const tileLayerOptions: {
	label: string;
	value: TileLayerName | '';
}[] = [
	{
		label: 'Ne pas afficher',
		value: 'unset',
	},
	{
		label: 'Open street map',
		value: 'osm',
	},
	{
		label: 'Satellite',
		value: 'satellite',
	},
];

/**
 * Réglages propres au fond OpenStreetMap, rendus à l'intérieur du choix dont ils dépendent (voir
 * `MapForm`, même parti pris). Chaque contrôle n'écrit qu'un champ (`setOsmOptions` fusionne) mais
 * lit la valeur EFFECTIVE via `resolveOsmOptions` : un réglage jamais touché est absent de la
 * configuration, alors qu'un interrupteur a besoin d'un booléen.
 */
// Pas d'`observer` : le `React.memo` qu'il pose bloquerait le `forceUpdate` du parent, seul mécanisme
// de rafraîchissement ici — l'adapter n'est pas observable.
function AdvancedOptionsSection() {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();
	const [open, setOpen] = React.useState(false);

	const options = resolveOsmOptions(editor.getOsmOptions());
	const allowPitch = editor.getAllowPitch();

	return (
		<View gap="sm">
			<View
				row
				centerV
				gap={6}
				pointer
				bold
				size={12.5}
				color={tokens.text.muted}
				onClick={() => setOpen(!open)}
			>
				<DownOutlined
					style={{ fontSize: 9, transition: 'transform .2s ease' }}
					rotate={open ? 0 : -90}
				/>

				Options avancées
			</View>

			{open && (
				<View gap="md" pl="sm" style={{ borderLeft: `2px solid ${tokens.border.default}` }}>
					<Field label="Style du fond">
						<Segmented
							block
							size="small"
							value={options.style}
							onChange={value => editor.setOsmOptions({ style: value as OsmStyleName })}
							options={osmStyleOptions}
						/>
					</Field>

					<Field
						label="Langue des libellés"
						help="un lieu sans nom dans cette langue garde son nom local."
					>
						<Select
							value={options.language ?? LOCAL_LANGUAGE_VALUE}
							onChange={value => editor.setOsmOptions({
								// `null`, pas `undefined` : voir `OsmBackgroundOptions.language`.
								language: value === LOCAL_LANGUAGE_VALUE ? null : (value as string),
							})}
							options={osmLanguageOptions}
						/>
					</Field>

					<Field label="Libellés affichés">
						<View gap="xs">
							<SwitchField
								label="Noms de lieux"
								help="villes, quartiers, pays, mers, aéroports."
								value={options.placeLabels}
								onChange={value => editor.setOsmOptions({ placeLabels: value })}
							/>

							<SwitchField
								label="Noms de rues"
								help="voies, numéros de route, sens uniques."
								value={options.roadLabels}
								onChange={value => editor.setOsmOptions({ roadLabels: value })}
							/>
						</View>
					</Field>

					<SwitchField
						label="Bâtiments en relief"
						help={allowPitch
							? 'donne du volume au bâti ; visible en inclinant la caméra.'
							: 'nécessite « Activer la 3D », ci-dessus.'}
						value={options.buildings3d}
						disabled={!allowPitch}
						onChange={value => editor.setOsmOptions({ buildings3d: value })}
					/>
				</View>
			)}
		</View>
	);
}

const osmStyleOptions: {
	label: string;
	value: OsmStyleName;
}[] = [
	{ label: 'Clair', value: 'light' },
	{ label: 'Sombre', value: 'dark' },
	{ label: 'Sobre', value: 'sober' },
	{ label: 'Détaillé', value: 'detailed' },
];

/** `Select` ne sait pas porter `null` comme valeur d'option. */
const LOCAL_LANGUAGE_VALUE = '';

/** Codes OSM (`name:<langue>`) — toute langue présente dans OSM fonctionnerait, la liste est courte
 * par choix. */
const osmLanguageOptions: {
	label: string;
	value: string;
}[] = [
	{ label: 'Nom local', value: LOCAL_LANGUAGE_VALUE },
	{ label: 'Français', value: 'fr' },
	{ label: 'English', value: 'en' },
	{ label: 'Deutsch', value: 'de' },
	{ label: 'Español', value: 'es' },
	{ label: 'Italiano', value: 'it' },
	{ label: 'Nederlands', value: 'nl' },
	{ label: 'Português', value: 'pt' },
	{ label: 'Русский', value: 'ru' },
	{ label: '日本語', value: 'ja' },
	{ label: '中文', value: 'zh' },
	{ label: 'العربية', value: 'ar' },
];
